import { action, set } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert, didUpdate } from '@ember/render-modifiers';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { PrismSelectInput } from '@mylighthouse/prism-ember';
import dayjs from 'dayjs';
import { t } from 'ember-intl';

interface GeneralDatepickersFieldsDropdownsMonthRangeSignature {
  Args: {
    isSecondary: unknown;
    selectRange: unknown;
    selectedRange: unknown;
    startDateHelpText: unknown;
    unsafe_matchTriggerWidth: unknown;
  };
  Element: HTMLDivElement;
}

export default class GeneralDatepickersFieldsDropdownsMonthRange extends Component<GeneralDatepickersFieldsDropdownsMonthRangeSignature> {
  @service declare dates: Services['dates'];

  @tracked endMonth = dayjs();
  @tracked startMonth = dayjs();

  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dropdownEndMonths() {
    const months = [];
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    let month = this.args.minDate;
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    let max = this.args.maxDate;

    const { startMonth } = this;

    // Start from the min date or from the start date
    if (startMonth) {
      month = startMonth;

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { rangeTime, rangePeriod } = this.maxRangeInfo;

      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      if (max > startMonth.add(rangeTime, rangePeriod)) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        max = startMonth.add(rangeTime, rangePeriod);
      }
    }

    while (month < max) {
      months.push({
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        id: month.format('YYYY-MM'),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        label: month.format(this.localisedFormat),
      });
      // No clone since we need it to increment
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      month = month.add(1, 'month');
    }

    return months;
  }

  // The options for the initial month in the monthpicker
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dropdownStartMonths() {
    const months = [];
    // The start month does not need to keep the max range into account
    // The end date always adapts to the what was selected here if needed
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    let month = this.args.minDate;
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const max = this.args.maxDate;

    // Continue adding months until the endDate is reached or until the max is reached
    while (month < max) {
      months.push({
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        id: month.format('YYYY-MM'),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        label: month.format(this.localisedFormat),
      });
      // No clone since we need it to increment
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      month = month.add(1, 'month');
    }

    return months;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get localisedFormat() {
    return this.dates.partialDateFormat?.YEAR_AND_MONTH_LONG;
  }

  // Get the amount and period of the maxRange
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get maxRangeInfo() {
    // The max range we pass is always an amount and a period
    // Examples: 1 year, 12 months, 5 days, ...
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const rangeSplit = this.args.maxRange.split(' ');
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
    const rangeTime = parseInt(rangeSplit[0], 10);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const rangePeriod = rangeSplit[1];

    return {
      rangeTime,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      rangePeriod,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedEndMonth() {
    const months = this.dropdownEndMonths;
    const targetMonth = this.endMonth.format('YYYY-MM');

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return GeneralDatepickersFieldsDropdownsMonthRange.getSelectedMonth(
      months,
      targetMonth,
    );
  }

  // We want to show the currently selected month immediately, not simply start at the first month
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedStartMonth() {
    const months = this.dropdownStartMonths;
    const targetMonth = this.startMonth.format('YYYY-MM');
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return GeneralDatepickersFieldsDropdownsMonthRange.getSelectedMonth(
      months,
      targetMonth,
    );
  }

  /**
   * This function looks if the target month is in the current months
   * If not, it return the first month as a default
   * it is used for the monthpicker options
   * @param { array } months        : list of possible months
   * @param { string } targetMonth  : month we are looking for, formatted as "YYYY-MM" to easily compare with the month ids
   */
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  static getSelectedMonth(months, targetMonth) {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return months.find((month) => month.id === targetMonth) || months[0];
  }

  @action
  // @ts-expect-error: Incorrect type
  selectEndMonth(month): void {
    const newRange = {};
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
    newRange.start = dayjs(this.selectedStartMonth.id).startOf('month');
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
    newRange.end = dayjs(month.id).endOf('month');
    // @ts-expect-error: Incorrect type
    this.args.selectRange({ moment: newRange }, true);
  }

  @action
  // @ts-expect-error: Incorrect type
  selectStartMonth(month): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
    const monthMoment = dayjs(month.id).startOf('month');
    set(this, 'startMonth', monthMoment);
    const newRange = {};
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
    newRange.start = dayjs(month.id).startOf('month');
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
    newRange.end = dayjs(this.selectedEndMonth.id).endOf('month');

    // @ts-expect-error: Incorrect type
    this.args.selectRange({ moment: newRange }, true);
  }

  @action
  updateMonths(): void {
    // @ts-expect-error: Incorrect type
    set(this, 'startMonth', this.args.selectedRange.start);
    // @ts-expect-error: Incorrect type
    set(this, 'endMonth', this.args.selectedRange.end);
  }

  <template>
    <div
      class="monthpicker-container flex flex-row"
      {{didInsert this.updateMonths}}
      {{didUpdate this.updateMonths @selectedRange}}
      ...attributes
    >
      <div class="monthpicker flex flex-col">
        <label class="text-200--regular">
          {{t "events.start_date"}}
        </label>
        <PrismSelectInput
          {{! @glint-expect-error: Incorrect type }}
          @isSecondary={{@isSecondary}}
          @onChange={{this.selectStartMonth}}
          @options={{this.dropdownStartMonths}}
          {{! @glint-expect-error: Incorrect type }}
          @unsafe_matchTriggerWidth={{@unsafe_matchTriggerWidth}}
          @value={{this.selectedStartMonth}}
        />
        {{#if @startDateHelpText}}
          <p class="text-200--regular text-neutral-subdued mt-100 ml-50">
            {{! @glint-expect-error: Incorrect type }}
            {{@startDateHelpText}}
          </p>
        {{/if}}
      </div>
      <div class="monthpicker flex flex-col">
        <label class="text-200--regular">
          {{t "events.end_date"}}
        </label>
        <PrismSelectInput
          {{! @glint-expect-error: Incorrect type }}
          @isSecondary={{@isSecondary}}
          @onChange={{this.selectEndMonth}}
          @options={{this.dropdownEndMonths}}
          {{! @glint-expect-error: Incorrect type }}
          @unsafe_matchTriggerWidth={{@unsafe_matchTriggerWidth}}
          @value={{this.selectedEndMonth}}
        />
      </div>
    </div>
  </template>
}
